import { type FC, type ReactNode, useMemo } from "react";

import { SectionRole } from "../../../shared/types/roles";

import { SectionUserCard } from "./SectionUserCard";

import { t } from "i18n/i18n";
import { PersonStatusIcon } from "mds/icons";
import { selectEnabledUserIdsInRoleForCurrentSection, selectOnlineUserIds } from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { getSectionResourceRole } from "utils/section";

interface SectionRoleCardProps {
  sectionRole: SectionRole;
  className?: string;
  headerIcon?: ReactNode;
  separateUsersInClassSession?: boolean;
  showEmpty?: boolean;
  showOnlineCount?: boolean;
  startOpen?: boolean;
}

export const SectionRoleCard: FC<SectionRoleCardProps> = ({
  sectionRole,
  className,
  headerIcon,
  separateUsersInClassSession = false,
  showEmpty = false,
  showOnlineCount = false,
  startOpen = false,
}) => {
  const userIdsInRole = useListSelector((state) =>
    selectEnabledUserIdsInRoleForCurrentSection(state, sectionRole),
  );
  const onlineUserIds = useListSelector(selectOnlineUserIds);
  const onlineUserIdsSet = useMemo(() => new Set(onlineUserIds), [onlineUserIds]);
  const onlineCount = userIdsInRole.filter((userId) => onlineUserIdsSet.has(userId)).length;
  const role = getSectionResourceRole(sectionRole);

  return (
    <SectionUserCard
      className={className}
      headerCount={
        showOnlineCount ? `${onlineCount}/${userIdsInRole.length}` : `${userIdsInRole.length}`
      }
      headerIcon={
        headerIcon || (
          <PersonStatusIcon size="xs" statusColor={onlineCount > 0 ? "green" : "gray"} />
        )
      }
      headerTitle={t(`roles.section_${role}_plural`)}
      separateUsersInClassSession={separateUsersInClassSession}
      showEmpty={showEmpty}
      startOpen={startOpen}
      userIds={userIdsInRole}
    />
  );
};
